import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PrivyProvider } from "@privy-io/react-auth";
import type { Wallet, WalletAccount } from "@wallet-standard/base";
import type { AuthResponse } from "@redchain/contracts";
import { api } from "../api/client";
import { env } from "../env";
import { PrivyBridge, type PrivyApi } from "./PrivyBridge";
import type { GameWallet, WalletContextValue } from "./types";
import {
  connectWallet,
  disconnectWallet,
  externalGameWallet,
  lastExternalWallet,
  listExternalWallets,
  onWalletsChanged,
} from "./walletStandard";

const WalletContext = createContext<WalletContextValue | null>(null);

/** Wallet Standard / Phantom reject SIWS when the extension is on mainnet and we ask for devnet. */
function friendlyWalletError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/chain id does not match/i.test(msg)) {
    return `This site uses Solana ${env.cluster}. Switch your wallet to ${env.cluster} (Phantom: Settings → Developer Settings → Testnet Mode / Change Network), then try again.`;
  }
  return msg;
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet outside WalletProvider");
  return ctx;
}

/**
 * Privy embedded Solana wallets (email, Google, Apple, Discord; created for every user at
 * first login) and Wallet Standard wallets behind one `GameWallet` interface (§7.7, WEB-13).
 */
export function WalletProvider({ children }: { children: ReactNode }) {
  if (!env.privyAppId) return <WalletState privyEnabled={false}>{children}</WalletState>;
  return (
    <PrivyProvider
      appId={env.privyAppId}
      config={{
        loginMethods: ["email", "google", "apple", "discord"],
        embeddedWallets: {
          solana: { createOnLogin: "all-users" },
          ethereum: { createOnLogin: "off" },
        },
        appearance: { walletChainType: "solana-only", theme: "dark" },
      }}
    >
      <WalletState privyEnabled>{children}</WalletState>
    </PrivyProvider>
  );
}

function WalletState({ children, privyEnabled }: { children: ReactNode; privyEnabled: boolean }) {
  const [privy, setPrivy] = useState<PrivyApi | null>(null);
  const [external, setExternal] = useState<{ wallet: Wallet; account: WalletAccount } | null>(null);
  const [auth, setAuth] = useState<AuthResponse | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalList, setExternalList] = useState(() => listExternalWallets());
  const [restored, setRestored] = useState(false);
  const privyAuthStarted = useRef(false);

  useEffect(() => onWalletsChanged(() => setExternalList(listExternalWallets())), []);

  useEffect(() => {
    api.onSignedOut = () => setAuth(null);
  }, []);

  const privyWallet = useMemo<GameWallet | null>(() => {
    if (!privy?.authenticated || !privy.embeddedAddress) return null;
    const p = privy;
    const address = p.embeddedAddress!;
    return {
      address,
      kind: "privy",
      async signIn() {
        const token = await p.getAccessToken();
        if (!token) throw new Error("Privy session expired; please log in again");
        const res = await api.authPrivy(token);
        api.setToken(res.accessToken);
        return res;
      },
      signTransaction: (bytes) => p.signTransaction(bytes),
    };
  }, [privy]);

  const externalWallet = useMemo<GameWallet | null>(() => (external ? externalGameWallet(external.wallet, external.account) : null), [external]);

  const wallet = externalWallet ?? privyWallet;

  // Privy login → exchange the Privy token for an API session (§20.1 /auth/privy).
  useEffect(() => {
    if (!privyWallet || external || auth || privyAuthStarted.current) return;
    privyAuthStarted.current = true;
    setAuthenticating(true);
    privyWallet
      .signIn()
      .then((res) => {
        setAuth(res);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        setAuthenticating(false);
        privyAuthStarted.current = false;
      });
  }, [privyWallet, external, auth]);

  // Reload: reconnect the last external wallet silently and rotate the refresh cookie.
  useEffect(() => {
    if (restored) return;
    setRestored(true);
    const name = lastExternalWallet();
    if (!name) return;
    void (async () => {
      try {
        const conn = await connectWallet(name, true);
        const token = await api.refresh();
        if (!token) return;
        setExternal(conn);
        const me = await api.me();
        if (me.wallet !== conn.account.address) {
          await api.logout();
          return;
        }
        setAuth({ accessToken: token, player: { registered: me.registered }, wallet: me.wallet });
      } catch {
        /* user will log in again */
      }
    })();
  }, [restored]);

  const connectExternal = useCallback(async (name: string) => {
    setAuthenticating(true);
    setError(null);
    try {
      const conn = await connectWallet(name);
      const gw = externalGameWallet(conn.wallet, conn.account);
      const res = await gw.signIn();
      setExternal(conn);
      setAuth(res);
    } catch (e) {
      setError(friendlyWalletError(e));
    } finally {
      setAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    if (external) await disconnectWallet(external.wallet);
    if (privy?.authenticated) await privy.logout();
    setExternal(null);
    setAuth(null);
  }, [external, privy]);

  const value: WalletContextValue = {
    ready: privyEnabled ? !!privy?.ready : true,
    wallet,
    auth,
    authenticating,
    error,
    externalWallets: externalList.map((w) => ({ name: w.name, icon: w.icon })),
    loginWithPrivy: () => {
      if (!privy) {
        setError("Email and social login aren't configured (VITE_PRIVY_APP_ID).");
        return;
      }
      privy.login();
    },
    connectExternal,
    logout,
    exportKey: wallet?.kind === "privy" && privy ? () => privy.exportKey() : null,
    markRegistered: () => setAuth((a) => (a ? { ...a, player: { registered: true } } : a)),
  };

  return (
    <WalletContext.Provider value={value}>
      {privyEnabled && <PrivyBridge onChange={setPrivy} />}
      {children}
    </WalletContext.Provider>
  );
}
